import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CurrencyTextField from './CurrencyTextField';
import PartCategoryField from './PartCategoryField';
import PartLabelsField from './PartLabelsField';
import { partService } from '../services/partService';

const emptyPartForm = () => ({
  useCustomPartNumber: false,
  part_number: '',
  description: '',
  quantity: '1',
  unit_price: '',
  category: '',
  labels: []
});

const NewPartModal = ({ open, onClose, onCreated }) => {
  const [formData, setFormData] = useState(emptyPartForm());
  const [categories, setCategories] = useState([]);
  const [labelOptions, setLabelOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.all([partService.getCategories(), partService.getLabels()])
      .then(([catRes, labRes]) => {
        if (cancelled) return;
        setCategories(Array.isArray(catRes?.data) ? catRes.data : []);
        setLabelOptions(Array.isArray(labRes?.data) ? labRes.data : []);
      })
      .catch(() => {
        if (cancelled) return;
        setCategories([]);
        setLabelOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleClose = () => {
    if (!saving) {
      setFormData(emptyPartForm());
      setError('');
      onClose();
    }
  };

  const handleCreate = async () => {
    const { useCustomPartNumber, part_number, description } = formData;
    if (!description?.trim()) {
      setError('Description is required.');
      return;
    }
    if (useCustomPartNumber && !part_number?.trim()) {
      setError('Part number is required when using a custom part number.');
      return;
    }
    if (useCustomPartNumber && part_number?.trim() && !/^[A-Za-z0-9-]+$/.test(part_number.trim())) {
      setError('Part number may contain only letters, numbers, and dashes.');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        description: description.trim(),
        unit_price: formData.unit_price ? parseFloat(formData.unit_price) : null
      };
      if (useCustomPartNumber && part_number?.trim()) {
        payload.part_number = part_number.trim();
      }
      const cat = formData.category?.trim();
      if (cat) payload.category = cat;
      payload.label_ids = (formData.labels || []).map((l) => l.id).filter(Boolean);
      const res = await partService.create(payload);
      const createdPart = res?.data ?? res;
      handleClose();
      const qty = formData.quantity != null && formData.quantity !== '' ? parseFloat(formData.quantity) : 1;
      const lineItemFields = {
        description: formData.description?.trim() ?? '',
        quantity: Number.isFinite(qty) && qty > 0 ? qty : 1
      };
      onCreated?.(createdPart, lineItemFields);
    } catch (err) {
      console.error('Error creating part:', err);
      const msg = err.response?.data?.message || 'Failed to create part';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Part</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.useCustomPartNumber}
                  onChange={(e) => setFormData((f) => ({ ...f, useCustomPartNumber: e.target.checked }))}
                  size="small"
                />
              }
              label="Use custom part number"
            />
          </Grid>
          {formData.useCustomPartNumber ? (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Part Number"
                value={formData.part_number}
                onChange={(e) => setFormData((f) => ({ ...f, part_number: e.target.value }))}
                placeholder="e.g. SS-001"
                helperText="Letters, numbers, and dashes only"
              />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Part Number"
                value="Auto-generated"
                disabled
                helperText="Part number will be auto-generated"
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              multiline
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
              placeholder="Part description (required)"
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              inputProps={{ min: 0.001, step: 1 }}
              label="Quantity"
              value={formData.quantity}
              onChange={(e) => setFormData((f) => ({ ...f, quantity: e.target.value }))}
              placeholder="1"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CurrencyTextField
              fullWidth
              size="small"
              label="Unit Price"
              value={formData.unit_price}
              onChange={(e) => setFormData((f) => ({ ...f, unit_price: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <PartCategoryField
              value={formData.category}
              onChange={(category) => setFormData((f) => ({ ...f, category }))}
              options={categories}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <PartLabelsField
              value={formData.labels}
              onChange={(labels) => setFormData((f) => ({ ...f, labels }))}
              options={labelOptions}
              disabled={saving}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleCreate} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewPartModal;
